import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Grid,
  Typography,
  Button,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { OpenInNew, Cancel } from '@mui/icons-material'
import dateFnsFormat from 'date-fns/format'
import BaseDialog from '../BaseDialog'

// Control status constants
const CONTROL_STATUS = {
  ACTIVE: 1,
  CANCELED: 2,
}

// Conformity constants
const CONFORMITY = {
  WAITING_RESULT: 2,
}

/**
 * Control Details Preview for CATP
 * Renders control summary in calendar modal or inline in list
 * @param {boolean} inline - When true, renders in compact mode without close button
 * @param {boolean} isOverdue - When true, shows a late/overdue badge
 */
export default function ControlDetailsPreview({
  control,
  onViewFullDetails,
  onCancel,
  onAssign,
  controllers = [],
  controllerColors,
  mainColor = '#1976d2',
  inline = false,
  isOverdue = false,
}) {
  const { t } = useTranslation()
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [selectedControllerId, setSelectedControllerId] = useState('')

  const handleCancelClick = (e) => {
    if (e) e.stopPropagation()
    setConfirmDialogOpen(true)
  }

  const handleConfirmCancel = () => {
    setConfirmDialogOpen(false)
    if (onCancel) onCancel()
  }

  const handleAssignClick = (e) => {
    if (e) e.stopPropagation()
    setSelectedControllerId('')
    setAssignDialogOpen(true)
  }

  const handleConfirmAssign = () => {
    if (selectedControllerId && onAssign) {
      onAssign(selectedControllerId)
    }
    setAssignDialogOpen(false)
    setSelectedControllerId('')
  }

  const controllerName = control.controller
    ? `${control.controller.firstName} ${control.controller.lastName}`
    : t('common.user-does-not-exist')

  const controllerColor = control.controller
    ? controllerColors[control.controller.id] || '#757575'
    : '#757575'

  const formatDate = (dateTime, date) => {
    try {
      if (dateTime) {
        return dateFnsFormat(new Date(dateTime), 'dd/MM/yyyy HH:mm')
      }
      if (date) {
        return dateFnsFormat(new Date(date), 'dd/MM/yyyy')
      }
    } catch {
      return 'N/A'
    }
    return 'N/A'
  }

  const renderConformity = (conformity) => {
    if (conformity === true || conformity === 1) {
      return <Chip size="small" label={t('conformity.valid')} color="success" />
    }
    if (conformity === false || conformity === 0) {
      return <Chip size="small" label={t('conformity.invalid')} color="error" />
    }
    return (
      <Chip
        size="small"
        label={t('conformity.waiting-result')}
        color="warning"
      />
    )
  }

  // Typography variants based on inline mode
  const labelVariant = inline ? 'caption' : 'body2'
  const valueVariant = inline ? 'body2' : 'body1'
  const spacing = inline ? 1.5 : 2

  const isCanceled = control.status === CONTROL_STATUS.CANCELED

  return (
    <Box sx={{ pt: inline ? 1 : 0 }}>
      {/* Status Badges */}
      {(isOverdue || isCanceled) && (
        <Box sx={{ mb: spacing, display: 'flex', gap: 1 }}>
          {isCanceled ? (
            <Chip
              size="small"
              label={t('calendar.status.canceled')}
              color="default"
            />
          ) : isOverdue ? (
            <Chip
              size="small"
              label={t('calendar.status.late')}
              color="error"
            />
          ) : null}
        </Box>
      )}
      {/* Control Info Section */}
      <Grid container spacing={inline ? 1 : 2}>
        {/* Left Column */}
        <Grid item xs={12} md={6}>
          {/* Date */}
          <Box sx={{ mb: spacing }}>
            <Typography variant={labelVariant} color="text.secondary">
              {t('common.date')}
            </Typography>
            <Typography variant={valueVariant}>
              {formatDate(control.dateTime, control.date)}
            </Typography>
          </Box>

          {/* Controller */}
          <Box sx={{ mb: spacing }}>
            <Typography variant={labelVariant} color="text.secondary">
              {t('common.controlBy')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: inline ? 12 : 14,
                  height: inline ? 12 : 14,
                  borderRadius: 0.5,
                  backgroundColor: controllerColor,
                }}
              />
              <Typography variant={valueVariant}>{controllerName}</Typography>
              {/* Show assign button if no controller and control is pending */}
              {!control.controller &&
                control.conformity === CONFORMITY.WAITING_RESULT &&
                onAssign && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleAssignClick}
                    sx={{
                      color: mainColor,
                      minWidth: 'auto',
                      p: 0.5,
                      fontSize: '0.75rem',
                    }}
                  >
                    {t('calendar.control-details.assign')}
                  </Button>
                )}
            </Box>
          </Box>

          {/* Control Location */}
          {control.location && (
            <Box sx={{ mb: spacing }}>
              <Typography variant={labelVariant} color="text.secondary">
                {t('common.control-location')}
              </Typography>
              <Typography variant={valueVariant}>
                {control.location.name || 'N/A'}
              </Typography>
            </Box>
          )}

          {/* Conformity */}
          <Box sx={{ mb: spacing }}>
            <Typography variant={labelVariant} color="text.secondary">
              {t('common.conformity')}
            </Typography>
            <Box sx={{ mt: 0.5 }}>{renderConformity(control.conformity)}</Box>
          </Box>
        </Grid>

        {/* Right Column - Vehicle Info */}
        <Grid item xs={12} md={6}>
          {/* Chassis */}
          {control.chassis && (
            <>
              <Box sx={{ mb: spacing }}>
                <Typography variant={labelVariant} color="text.secondary">
                  {t('common.chassis')}
                </Typography>
                <Typography variant={valueVariant}>
                  {control.chassis.nChassis || 'N/A'}
                </Typography>
              </Box>

              <Box sx={{ mb: spacing }}>
                <Typography variant={labelVariant} color="text.secondary">
                  {t('common.nParc')}
                </Typography>
                <Typography variant={valueVariant}>
                  {control.chassis.nParc || 'N/A'}
                </Typography>
              </Box>
            </>
          )}

          {/* Vehicle Type */}
          {control.vehicleType && (
            <Box sx={{ mb: spacing }}>
              <Typography variant={labelVariant} color="text.secondary">
                {t('common.vehicleType')}
              </Typography>
              <Typography variant={valueVariant}>
                {control.vehicleType.title || 'N/A'}
              </Typography>
            </Box>
          )}

          {/* Constructor */}
          {control.vehicleType?.constructor && (
            <Box sx={{ mb: spacing }}>
              <Typography variant={labelVariant} color="text.secondary">
                {t('common.constructeur')}
              </Typography>
              <Typography variant={valueVariant}>
                {control.vehicleType.constructor.name || 'N/A'}
              </Typography>
            </Box>
          )}

          {/* Member (for member controls) */}
          {control.member && (
            <Box sx={{ mb: spacing }}>
              <Typography variant={labelVariant} color="text.secondary">
                {t('common.member')}
              </Typography>
              <Typography variant={valueVariant}>
                {control.member.name || 'N/A'}
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Action Buttons - different layout for inline mode */}
      {inline ? (
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}
        >
          {isOverdue && !isCanceled && onCancel && (
            <Button
              variant="text"
              size="small"
              color="error"
              startIcon={<Cancel fontSize="small" />}
              onClick={handleCancelClick}
            >
              {t('calendar.control-details.cancel')}
            </Button>
          )}
          {onViewFullDetails && (
            <Button
              variant="text"
              size="small"
              endIcon={<OpenInNew fontSize="small" />}
              onClick={(e) => {
                e.stopPropagation()
                onViewFullDetails()
              }}
              sx={{ color: mainColor }}
            >
              {t('calendar.control-details.view-full-details')}
            </Button>
          )}
        </Box>
      ) : (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            {isOverdue && !isCanceled && onCancel && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<Cancel />}
                onClick={handleCancelClick}
              >
                {t('calendar.control-details.cancel')}
              </Button>
            )}
            {onViewFullDetails && (
              <Button
                variant="contained"
                endIcon={<OpenInNew />}
                onClick={onViewFullDetails}
                sx={{
                  backgroundColor: mainColor,
                  '&:hover': {
                    backgroundColor: mainColor,
                    filter: 'brightness(0.9)',
                  },
                }}
              >
                {t('calendar.control-details.view-full-details')}
              </Button>
            )}
          </Box>
        </>
      )}

      {/* Cancel Confirmation Dialog */}
      <BaseDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleConfirmCancel}
        title={t('calendar.cancel.confirm.title')}
        openButtonTitle={t('common.yes')}
        closeButtonTitle={t('common.no')}
      >
        <Typography sx={{ pt: 2 }}>
          {t('calendar.cancel.confirm.message')}
        </Typography>
      </BaseDialog>

      {/* Assign Controller Dialog */}
      <BaseDialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        onConfirm={handleConfirmAssign}
        title={t('calendar.assign.title')}
        openButtonTitle={t('calendar.assign.confirm')}
        closeButtonTitle={t('common.cancel')}
        confirmDisabled={!selectedControllerId}
      >
        <Box sx={{ pt: 2, minWidth: 300 }}>
          <FormControl fullWidth>
            <InputLabel id="assign-controller-label">
              {t('calendar.assign.select-controller')}
            </InputLabel>
            <Select
              labelId="assign-controller-label"
              value={selectedControllerId}
              onChange={(e) => setSelectedControllerId(e.target.value)}
              label={t('calendar.assign.select-controller')}
            >
              {controllers.map((controller) => (
                <MenuItem key={controller.id} value={controller.id}>
                  {controller.firstName} {controller.lastName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </BaseDialog>
    </Box>
  )
}
